// src/components/home/Introduction.js
import React, { useEffect } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

const Counter = ({ from = 0, to, suffix = "", decimals = 0, duration = 2 }) => {
  const count = useMotionValue(from);
  const rounded = useTransform(count, (latest) =>
    suffix === "%"
      ? `${Math.round(latest)}%`
      : latest >= 1000 && suffix !== "%"
      ? `${Intl.NumberFormat("en-US", {
          notation: "compact",
          maximumFractionDigits: decimals,
        }).format(latest)}${suffix}`
      : `${latest.toFixed(decimals)}${suffix}`
  );

  useEffect(() => {
    const controls = animate(count, to, { duration, ease: "easeOut" });
    return controls.stop;
  }, [count, to, duration]);

  return <motion.span>{rounded}</motion.span>;
};

const Introduction = () => {
  return (
    <section className="relative bg-gradient-to-b from-[#050E24] via-[#0A2A66] to-[#050E24] text-white py-28 overflow-hidden">
      {/* Layered lighting accents */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(77,178,211,0.25),transparent_70%)] blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_bottom_right,rgba(61,143,170,0.15),transparent_70%)] blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto text-center px-6">
        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-8 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-white">Redefining </span>
          <span className="bg-gradient-to-r from-[#4DB2D3] via-[#6CD6E6] to-[#A5F5E2] bg-clip-text text-transparent">
            On-Chain Capital Management
          </span>
        </motion.h2>

        {/* Paragraph */}
        <motion.p
          className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          QuantNexus Capital integrates Enzyme’s audited on-chain infrastructure
          with proprietary strategy automation. Each QNC vault operates as a
          fully autonomous hedge fund — executing, reporting, and enforcing
          performance logic directly on-chain while allocators retain full
          custody through Kraken Institutional.
        </motion.p>

        {/* --- TRUST METRICS --- */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto mb-16 text-white/80"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition">
            <h3 className="text-3xl font-bold text-[#4DB2D3] mb-1">
              <Counter to={400} suffix="+" duration={2.5} />
            </h3>
            <p className="text-sm uppercase tracking-wide text-white/70">
              Verified Depositors
            </p>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition">
            <h3 className="text-3xl font-bold text-[#4DB2D3] mb-1">
              $<Counter to={1000000000} suffix="+" duration={2.5} />
            </h3>
            <p className="text-sm uppercase tracking-wide text-white/70">
              On-Chain Traded Volume
            </p>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition">
            <h3 className="text-3xl font-bold text-[#4DB2D3] mb-1">
              <Counter to={100} suffix="%" duration={2.5} />
            </h3>
            <p className="text-sm uppercase tracking-wide text-white/70">
              Non-Custodial Architecture
            </p>
          </div>
        </motion.div>

        {/* Divider line */}
        <motion.div
          className="w-24 h-[2px] mx-auto mb-16 bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA]"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        />

        {/* Sub-highlights */}
        <motion.div
          className="grid md:grid-cols-3 gap-10 text-left md:text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {[
            {
              title: "Regulatory-Aligned",
              text: "Structured under global frameworks such as MiCA and FATF, with qualified-custodian integration through Kraken Institutional.",
            },
            {
              title: "Automated Infrastructure",
              text: "Vault deployment, fee logic, and reporting are executed entirely on-chain through Enzyme’s audited smart contracts.",
            },
            {
              title: "Transparent by Design",
              text: "Every allocation, trade, and fee event is verifiable in real time — replacing legacy statements with immutable, on-chain data.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="relative group p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md hover:border-[#4DB2D3]/30 hover:bg-white/10 transition-all duration-300"
              whileHover={{ y: -4 }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] blur-2xl transition duration-500"></div>

              <h3 className="text-xl font-semibold mb-3 text-[#4DB2D3]">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-white/80">
                {item.text}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Introduction;
